import { useContext, useState } from "react";
import { Plus } from "lucide-react";
import { VerifyContext } from "app/VerifyContext";
import { ResourcesProvider, useResources } from "./modal/ResourcesContext";
import ResourceCard from "./ui/ResourceCard";
import ResourceFormModal from "./ui/ResourceFormModal";
import type { ResourceItem } from "./api/ResourcesApi";

function ResourcesBody() {
  const { superAdmin } = useContext(VerifyContext);
  const {
    resources, loading, error, includeArchived, setIncludeArchived,
    createResource, updateResource, archiveResource, restoreResource, deleteResource,
  } = useResources();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ResourceItem | null>(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(resource: ResourceItem) {
    setEditing(resource);
    setFormOpen(true);
  }

  async function handleSave(data: any) {
    if (editing) await updateResource(editing.id, data);
    else await createResource(data);
  }

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-foreground font-inter">Resources</h1>
          <p className="text-sm text-muted-foreground font-inter mt-0.5">
            Guides, handbooks and references for the team
          </p>
        </div>

        {superAdmin && (
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs text-muted-foreground font-inter cursor-pointer">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchived(e.target.checked)}
                className="rounded border-border bg-surface-muted text-primary focus:ring-0"
              />
              Show archived
            </label>
            <button
              onClick={openCreate}
              className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground text-sm font-medium rounded-xl font-inter"
            >
              <Plus className="w-4 h-4" />
              Add Resource
            </button>
          </div>
        )}
      </div>

      {error && <p className="text-sm text-destructive font-inter">{error}</p>}

      {loading ? (
        <p className="text-sm text-muted-foreground font-inter">Loading…</p>
      ) : resources.length === 0 ? (
        <p className="text-sm text-muted-foreground font-inter">No resources available yet.</p>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {resources.map((r) => (
            <ResourceCard
              key={r.id}
              resource={r}
              isAdmin={superAdmin}
              onEdit={openEdit}
              onToggleEnabled={(res) => updateResource(res.id, { is_enabled: !res.is_enabled })}
              onArchive={(res) => archiveResource(res.id)}
              onRestore={(res) => restoreResource(res.id)}
              onDelete={(res) => deleteResource(res.id)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <ResourceFormModal
          initial={editing}
          onSave={handleSave}
          onClose={() => setFormOpen(false)}
        />
      )}
    </div>
  );
}

export default function ResourcesPage() {
  return (
    <ResourcesProvider>
      <ResourcesBody />
    </ResourcesProvider>
  );
}
